/**
 * Card Component
 *
 * A reusable card container component that follows our design system.
 * Supports different variants, padding sizes, and interactive states.
 */

import React from 'react';
import { cn } from '@/lib/utils';

// =============================================================================
// TYPES
// =============================================================================

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Visual variant of the card
   */
  variant?: 'default' | 'bordered' | 'elevated' | 'flat';

  /**
   * Padding size
   */
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Make card interactive (hoverable/clickable)
   */
  interactive?: boolean;
}

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Padding size (inherits from parent Card if not specified)
   */
  padding?: 'sm' | 'md' | 'lg';
}

export interface CardContentProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Padding size (inherits from parent Card if not specified)
   */
  padding?: 'sm' | 'md' | 'lg';
}

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Padding size (inherits from parent Card if not specified)
   */
  padding?: 'sm' | 'md' | 'lg';
}

// =============================================================================
// STYLES
// =============================================================================

/**
 * Get the CSS classes for card variants
 */
const getVariantClasses = (variant: CardProps['variant']) => {
  switch (variant) {
    case 'default':
      return 'bg-[var(--surface-card)] border border-[var(--border-default)]';
    case 'bordered':
      return 'bg-[var(--surface-card)] border-2 border-[var(--border-default)]';
    case 'elevated':
      return 'bg-[var(--surface-card)] border border-[var(--border-default)] shadow-lg';
    case 'flat':
      return 'bg-[var(--surface-bg)]';
    default:
      return 'bg-[var(--surface-card)] border border-[var(--border-default)]';
  }
};

/**
 * Get the CSS classes for padding sizes
 */
const getPaddingClasses = (padding: CardProps['padding']) => {
  switch (padding) {
    case 'none':
      return 'p-0';
    case 'sm':
      return 'p-3';
    case 'md':
      return 'p-4';
    case 'lg':
      return 'p-6';
    case 'xl':
      return 'p-8';
    default:
      return 'p-4';
  }
};

const getHeaderPaddingClasses = (padding?: CardHeaderProps['padding']) => {
  switch (padding) {
    case 'sm':
      return 'px-3 py-2';
    case 'md':
      return 'px-4 py-3';
    case 'lg':
      return 'px-6 py-4';
    default:
      return 'px-4 py-3';
  }
};

const getContentPaddingClasses = (padding?: CardContentProps['padding']) => {
  switch (padding) {
    case 'sm':
      return 'px-3 py-2';
    case 'md':
      return 'px-4 py-3';
    case 'lg':
      return 'px-6 py-4';
    default:
      return 'px-4 py-3';
  }
};

const getFooterPaddingClasses = (padding?: CardFooterProps['padding']) => {
  switch (padding) {
    case 'sm':
      return 'px-3 py-2';
    case 'md':
      return 'px-4 py-3';
    case 'lg':
      return 'px-6 py-4';
    default:
      return 'px-4 py-3';
  }
};

// =============================================================================
// COMPONENTS
// =============================================================================

/**
 * Main Card component
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant = 'default',
      padding = 'md',
      interactive = false,
      children,
      ...props
    },
    ref
  ) => {
    const cardClassName = cn(
      // Base styles
      'rounded-lg transition-all duration-200',

      // Variant styles
      getVariantClasses(variant),

      // Padding styles
      getPaddingClasses(padding),

      // Interactive styles
      interactive && [
        'cursor-pointer',
        'hover:shadow-md',
        'hover:border-[var(--border-default)]',
        'active:scale-[0.99]',
      ],

      // Custom className
      className
    );

    return (
      <div ref={ref} className={cardClassName} {...props}>
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

/**
 * Card Header component
 */
export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, padding, children, ...props }, ref) => {
    const headerClassName = cn(
      'border-b border-[var(--border-default)]',
      getHeaderPaddingClasses(padding),
      className
    );

    return (
      <div ref={ref} className={headerClassName} {...props}>
        {children}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

/**
 * Card Content component
 */
export const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, padding, children, ...props }, ref) => {
    const contentClassName = cn(
      getContentPaddingClasses(padding),
      className
    );

    return (
      <div ref={ref} className={contentClassName} {...props}>
        {children}
      </div>
    );
  }
);

CardContent.displayName = 'CardContent';

/**
 * Card Footer component
 */
export const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, padding, children, ...props }, ref) => {
    const footerClassName = cn(
      'border-t border-[var(--border-default)]',
      getFooterPaddingClasses(padding),
      className
    );

    return (
      <div ref={ref} className={footerClassName} {...props}>
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

/**
 * Card Title component
 */
export const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => {
  return (
    <h3
      ref={ref}
      className={cn('text-lg font-semibold text-[var(--text-heading)]', className)}
      {...props}
    >
      {children}
    </h3>
  );
});

CardTitle.displayName = 'CardTitle';

/**
 * Card Description component
 */
export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={cn('text-sm text-[var(--text-secondary)]', className)}
      {...props}
    >
      {children}
    </p>
  );
});

CardDescription.displayName = 'CardDescription';

// =============================================================================
// EXPORTS
// =============================================================================

export default Card;
